/**
 * Feature Flags Configuration
 *
 * Centralized management of feature flags for the application.
 * Feature flags are read from environment variables at build time.
 *
 * Usage:
 *   import { isFeatureEnabled } from "@/shared/lib/featureFlags";
 *   if (isFeatureEnabled("DRE_ANALYSIS")) { ... }
 *
 * Environment variables follow the pattern: VITE_FEATURE_<FLAG_NAME>
 * Valid values: "true" or "false" (default: false)
 */

/**
 * Available feature flags.
 * Add new flags here with their corresponding environment variable name.
 */
export const FEATURE_FLAGS = {
    /** DRE (Demonstracao do Resultado do Exercicio) Analysis tool in the sidebar */
    DRE_ANALYSIS: "VITE_FEATURE_DRE_ANALYSIS",
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

/**
 * Check if a feature flag is enabled.
 *
 * @param flag - The feature flag key to check
 * @param defaultValue - Default value if env var is not set (default: false)
 * @returns true if the feature is enabled, false otherwise
 *
 * @example
 * if (isFeatureEnabled("DRE_ANALYSIS")) {
 *   // Render DRE Analysis component
 * }
 */
export function isFeatureEnabled(flag: FeatureFlag, defaultValue = false): boolean {
    const envVarName = FEATURE_FLAGS[flag];
    const envValue = import.meta.env[envVarName];

    // If not defined, return default
    if (envValue === undefined || envValue === "") {
        return defaultValue;
    }

    // Handle boolean and string values
    if (typeof envValue === "boolean") {
        return envValue;
    }

    return envValue === "true";
}

/**
 * Get all feature flags and their current status.
 * Useful for debugging and admin panels.
 */
export function getAllFeatureFlags(): Record<FeatureFlag, boolean> {
    return Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
        const flag = key as FeatureFlag;
        acc[flag] = isFeatureEnabled(flag);
        return acc;
    }, {} as Record<FeatureFlag, boolean>);
}
